import {
    countCharacters,
    isValidUrl,
    isValidAppStoreUrl,
    isValidPlayStoreUrl,
    isVideoType,
    isValueExist,
    testerFromArray,
} from '@util';

import { UPLOADABLE_IMAGE_VIDEO_TYPE, CHANNELS } from '@consts';
import { getAdConfig } from '@/utils/adsUtils';

import * as commonUtils from 'adriel_backandfrontcommon';
const { cutText } = commonUtils.adText;

const INSTAGRAM_ERROR_TYPES = {
    SIZE_RATIO: 'PROPOSAL.ads_invalid_video_dimension_size_facebook',
    DURATION: {
        key: 'PROPOSAL.ads_invalid_video_length',
        value: {
            value: 60,
        },
    },
    DURATION_CAROUSEL_VIDEO: {
        key: 'PROPOSAL.ads_invalid_video_length',
        value: {
            value: 120,
        },
    },
};

var defaultFunc = function (businessName) {
    var creative = {};
    this.params.forEach(({ key }) => {
        if (key === 'businessName') {
            creative[key] = businessName;
        } else {
            creative[key] = '';
        }
    });
    return creative;
};

//do not mutate original data
const VIDEO_CONFIG_INFO = Object.freeze({
    recommendedVideoLength: { value: 15, unit: 'seconds' },
    recommendedVideoRatio: { h: 4, v: 5 },
    videoSound: 'Enabled with captions included',
    recommendedVideoFormat: ['.mp4', '.mov'],
    videoResolution: { value: 600, unit: 'px' },
    videoMaxFileSize: { value: 4, unit: 'GB' },
    maxVideoLength: { value: 1, unit: 'min' },
});

const CTA_PARAM = Object.freeze({
    key: 'callToAction',
    name: 'COMMON.call_to_action_title',
    type: 'dropdown',
});

const addPhotoGroup = (ad) => {
    var defaultTo = R.defaultTo('');

    Object.assign(ad, {
        creativeType: 'carousel',
    });

    var creative = R.clone(ad.creative);
    var mainDescription = defaultTo(creative.description);
    var mainUrl = defaultTo(creative.url);
    var linkDesc = defaultTo(creative.linkDescription);
    const callToAction = creative.callToAction;

    var picks = R.compose(
        R.concat(['meta']),
        R.pluck('key'),
        R.reject(R.propEq('common', true)),
        R.prop('params'),
        getAdConfig
    )(ad);

    var picked = R.pickAll(picks, creative);

    const common = {
        url: creative.url,
        callToAction,
        description: linkDesc,
    };

    var newCreative = {
        mainDescription,
        mainUrl,
        cards: [
            {
                ...R.clone(picked),
                urlType: creative.urlType,
                ...common,
            },
            {
                ...R.clone(picked),
                ...common,
            },
        ],
    };

    var lastAdded = R.last(newCreative.cards);

    const skips = R.contains(R.__, [
        'options',
        'description',
        'callToAction',
        'url',
        'title',
        'appStoreUrl',
        'playStoreUrl',
        'webUrl',
        'formUrl',
    ]);

    R.compose(
        R.forEach(([key]) => (lastAdded[key] = '')),
        R.reject(R.propSatisfies(skips, '0')),
        Object.entries
    )(lastAdded);

    newCreative.cards.forEach((card) => {
        R.compose(
            R.forEach(([key]) => (card[key] = '')),
            R.filter(R.propEq('1', undefined)),
            Object.entries
        )(card);
    });

    ad.creative = newCreative;
    return ad;
};

const addCarouselGroup = (ad) => {
    var cards = ad.creative.cards;
    var card = R.compose(R.clone, R.last)(cards);
    const skips = R.contains(R.__, [
        'options',
        'callToAction',
        'title',
        'url',
        'description',
        'appStoreUrl',
        'playStoreUrl',
        'webUrl',
        'formUrl',
    ]);

    R.compose(
        R.forEach(([key]) => (card[key] = '')),
        R.reject(R.propSatisfies(skips, '0')),
        Object.entries
    )(card);
    card.url = card.url || ad.creative.mainUrl;
    cards.push(card);
    return ad;
};

const removeCarouselGroup = (ad, idx) => {
    var cards = ad.creative.cards;
    cards.splice(idx, 1);

    if (cards.length !== 1) {
        return ad;
    }
    //restore to single type
    Object.assign(ad, {
        creativeType: 'photo',
    });

    let picks = R.compose(
        R.concat(['meta', 'url']),
        R.pluck('key'),
        R.prop('params'),
        getAdConfig
    )(ad);

    let creative = R.pickAll(picks, cards[0]);

    R.compose(
        R.forEach(([key]) => (creative[key] = '')),
        R.filter(R.propEq('1', undefined)),
        Object.entries,
        R.pickAll(R.without(['mainDescription', 'mainUrl'], picks))
    )(creative);

    Object.assign(ad, {
        creative: {
            ...creative,
            description: ad.creative.mainDescription || '',
            linkDescription: creative.description,
        },
        callToAction: cards[0].callToAction,
    });

    return ad;
};

const getMissings = (creative = {}, keys = []) =>
    keys.reduce(
        (acc, paramName) =>
            !creative[paramName]
                ? acc.concat({ type: 'missing', paramName })
                : acc,
        []
    );

const validateUrls = (
    creative = {},
    { strictValidation = false, type = 'WEB', forbiddenUrls = [] } = {}
) => {
    let reasons = [];

    if (strictValidation === true) {
        const invalid = R.assoc('paramName', R.__, {
            type: 'invalid',
        });

        if (type === 'WEB') {
            const { url } = creative;

            if (
                isValueExist(forbiddenUrls) &&
                testerFromArray(forbiddenUrls, url)
            ) {
                reasons.push({
                    type: 'forbidden_url',
                    paramName: 'url',
                });
            } else if (!isValidUrl(url)) {
                reasons.push(invalid('url'));
            }
        } else if (type === 'APP') {
            const { appStoreUrl, playStoreUrl } = creative;
            if (
                !isValidAppStoreUrl(appStoreUrl) &&
                !isValidPlayStoreUrl(playStoreUrl)
            ) {
                reasons = [
                    ...reasons,
                    invalid('appStoreUrl'),
                    invalid('playStoreUrl'),
                ];
            }
        }
    }

    return reasons;
};

const returnReasons = (value) =>
    isValueExist(value)
        ? R.assoc('reasons', value, { value: false })
        : { value: true };

const APP_URL_CONFIG = Object.freeze([
    {
        key: 'webUrl',
        type: 'input',
        title: 'PROPOSAL.ad_config_web_url',
    },
    {
        key: 'appStoreUrl',
        title: 'PROPOSAL.ad_config_app_url',
        name: 'PROPOSAL.ad_config_appstore_url',
        type: 'input',
        // optional: true,
        slot: 'app',
    },
    {
        key: 'playStoreUrl',
        name: 'PROPOSAL.ad_config_playstore_url',
        type: 'input',
        // optional: true,
    },
    //TODO improve
    {
        type: 'input',
        name: 'PROPOSAL.ad_config_web_url_for_app',
        updateKey: 'webUrl',
        optional: true,
        show(creative, { appStoreUrl } = {}) {
            return appStoreUrl === true;
        },
    },
    {
        key: 'formUrl',
        type: 'input',
        title: 'PROPOSAL.ad_config_form_url',
        cannotedit: true,
    },
]);

const PHOTO_FORMAT = ['JPEG', 'PNG'];

const COPY_OMIT = R.omit(['meta', 'channel', 'enabled', 'creativeId']);

const attachUrls = R.compose(
    R.mergeLeft({
        url: '',
        playStoreUrl: '',
        appStoreUrl: '',
    }),
    R.defaultTo({})
);

const CONFIG = {
    all: {
        default: {
            params: [
                {
                    ...VIDEO_CONFIG_INFO,
                    key: 'media',
                    type: 'image',
                    format: PHOTO_FORMAT,
                    ratio: '600:314',
                    minSize: { width: 600, height: 314 },
                    maxFileSize: 1000,
                    accept: UPLOADABLE_IMAGE_VIDEO_TYPE,
                    name: 'PROPOSAL.ad_config_all_media',
                },
                {
                    key: 'mainText',
                    name: 'PROPOSAL.ad_config_all_main_title',
                    type: 'input',
                    maxLength: 40,
                },
                {
                    key: 'shortTitle',
                    name: 'PROPOSAL.ad_config_all_short_title',
                    type: 'input',
                    maxLength: 25,
                    show: R.compose(
                        R.flip(R.gt)(25),
                        countCharacters,
                        R.prop('mainText')
                    ),
                },
                {
                    key: 'subText',
                    // maxLength: 90,
                    type: 'textarea',
                    name: 'PROPOSAL.ad_config_all_description',
                },
                ...APP_URL_CONFIG,
            ],
        },
    },
    google: {
        ExpandedTextAd: {
            subChannel: ['search', 'display'],
            adType: 'link',
            getDefaultCreative(businessName) {
                var params = this.params;
                var creative = {};
                params.forEach(({ key }) => {
                    if (key === 'title1') {
                        creative[key] = businessName;
                    } else {
                        creative[key] = '';
                    }
                });
                return creative;
            },
            params: [
                {
                    key: 'title1',
                    name: 'PROPOSAL.ad_config_google_text_title_1',
                    type: 'input',
                    maxLength: 30,
                },
                {
                    key: 'title2',
                    name: 'PROPOSAL.ad_config_google_text_title_2',
                    type: 'input',
                    maxLength: 30,
                },
                {
                    key: 'title3',
                    name: 'PROPOSAL.ad_config_google_text_title_3',
                    type: 'input',
                    optional: true,
                    maxLength: 30,
                },
                {
                    key: 'description',
                    maxLength: 90,
                    type: 'textarea',
                    name: 'PROPOSAL.ad_config_google_text_description',
                },
                {
                    key: 'description2',
                    maxLength: 90,
                    type: 'textarea',
                    optional: true,
                    name: 'PROPOSAL.ad_config_google_text_description_2',
                },
                {
                    key: 'url',
                    type: 'input',
                    name: 'PROPOSAL.ad_config_all_url',
                    valueFromAll: R.prop('url'),
                },
                {
                    key: 'extensions',
                    defaultValue: { call: [], siteLink: [] },
                },
                ...APP_URL_CONFIG,
            ],
            executable(creative = {}, options) {
                let reasons = [];

                addMissing('titles', reasons);
                addMissing('descriptions', reasons);

                const siteLink = R.pathOr(
                    [],
                    ['extensions', 'siteLink'],
                    creative
                );
                const isSiteLinkEmpty = siteLink.some((link) => {
                    const emptyList = Object.keys(link).filter((key) => {
                        const value = (link[key] || '').trim();
                        return value === '';
                    });
                    if (isValueExist(emptyList)) {
                        return true;
                    }
                });

                if (isSiteLinkEmpty) {
                    reasons.push({ type: 'missing', paramName: 'extensions' });
                }

                const urls = validateUrls(creative, options);

                return returnReasons([...reasons, ...urls]);

                function addMissing(paramName, reasons) {
                    R.propOr([], paramName, creative).forEach(
                        (datum, index) => {
                            datum = datum || '';
                            datum = R.trim(datum);
                            if (!datum) {
                                reasons.push({
                                    type: 'missing',
                                    paramName: paramName,
                                    index,
                                });
                            }
                        }
                    );
                }
            },
            updateFrom(ad = {}, self = {}) {
                if (ad === self) {
                    return self;
                }
                const adCreative = ad.creative;
                const _text = R.compose(R.of, R.defaultTo(''));
                switch (ad.channel) {
                    case 'facebook':
                    case 'instagram':
                        var title, desc, url;
                        if (ad.creativeType === 'photo') {
                            title = adCreative.title;
                            desc = adCreative.description;
                            url = adCreative.url;
                        } else {
                            title = adCreative.cards[0].title;
                            desc = adCreative.mainDescription;
                            url = adCreative.mainUrl;
                        }

                        var [title1 = '', title2 = '', title3 = ''] =
                            cutText(_text(title), {
                                maxPart: 3,
                                maxLength: 30,
                            }) || [];

                        var [description = '', description2 = ''] =
                            cutText(_text(desc), {
                                maxPart: 2,
                                maxLength: 90,
                            }) || [];

                        return {
                            ...self,
                            creative: {
                                ...self.creative,
                                title1,
                                title2,
                                title3,
                                description,
                                description2,
                                url,
                            },
                        };
                    default:
                        return self;
                }
            },
            getDefault() {
                return {
                    channel: 'google',
                    creativeType: 'ExpandedTextAd',
                    creative: attachUrls({
                        titles: R.repeat('', 3),
                        descriptions: R.repeat('', 2),
                        extensions: { call: [], siteLink: [] },
                    }),
                };
            },
        },
        ResponsiveDisplayAd: {
            subChannel: ['display'],
            adType: 'link',
            getDefaultCreative: defaultFunc,
            params: [
                {
                    key: 'image',
                    type: 'image',
                    format: PHOTO_FORMAT,
                    ratio: '600:314',
                    minSize: { width: 600, height: 314 },
                    maxFileSize: 1000,
                    name: 'PROPOSAL.ad_config_google_display_image',
                },
                {
                    key: 'squareImage',
                    show: R.F,
                    type: 'image',
                },
                {
                    key: 'logoImage',
                    type: 'image',
                    format: PHOTO_FORMAT,
                    ratio: '1:1',
                    minSize: { width: 128, height: 128 },
                    optional: true,
                    maxFileSize: 1000,
                    name: 'PROPOSAL.ad_config_all_logo',
                },
                {
                    key: 'shortTitle',
                    type: 'input',
                    maxLength: 25,
                    name: 'PROPOSAL.ad_config_google_display_title',
                },
                {
                    key: 'longTitle',
                    type: 'input',
                    maxLength: 90,
                    name: 'PROPOSAL.ad_config_google_display_title_long',
                },
                {
                    key: 'businessName',
                    type: 'input',
                    maxLength: 25,
                    name: 'PROPOSAL.ad_config_business_name',
                },
                {
                    key: 'description',
                    type: 'textarea',
                    maxLength: 90,
                    name: 'PROPOSAL.ad_config_google_display_description',
                },
                // {
                //     key: 'url',
                //     name: 'PROPOSAL.ad_config_all_url',
                //     type: 'input',
                //     valueFromAll: R.prop('url'),
                // },
                ...APP_URL_CONFIG,
                CTA_PARAM,
            ],
            executable(creative = {}, options = {}) {
                let params = [
                    'image',
                    'shortTitle',
                    'businessName',
                    'description',
                ];

                if (options.strictValidation === true) {
                    params.push('longTitle');
                }

                const reasons = getMissings(creative, params);
                const urls = validateUrls(creative, options);

                return returnReasons([...reasons, ...urls]);
            },
            updateFrom(ad = {}, self = {}) {
                if (ad === self) {
                    return ad;
                }
                const adCreative = ad.creative;
                const _text = R.compose(R.of, R.defaultTo(''));
                switch (ad.channel) {
                    case 'facebook':
                    case 'instagram':
                        var title, desc, url, media;
                        if (ad.creativeType === 'photo') {
                            title = adCreative.title;
                            desc = adCreative.description;
                            url = adCreative.url;
                            media = adCreative.media;
                        } else {
                            title = adCreative.cards[0].title;
                            desc = adCreative.mainDescription;
                            media = adCreative.cards[0].media;
                            url = adCreative.mainUrl;
                        }

                        var [shortTitle = '', longTitle = ''] =
                            cutText(_text(title), {
                                maxPart: 2,
                                maxLength: 25,
                            }) || [];

                        var [description = ''] =
                            cutText(_text(desc), {
                                maxPart: 1,
                                maxLength: 90,
                            }) || [];

                        return {
                            ...self,
                            creative: {
                                ...self.creative,
                                image: isVideoType(media) ? self.image : media,
                                shortTitle,
                                longTitle,
                                description,
                                url,
                            },
                        };
                    case 'google':
                        var {
                            title1 = '',
                            title2 = '',
                            title3 = '',
                            url = '',
                        } = adCreative;

                        return {
                            ...self,
                            creative: {
                                ...self.creative,
                                shortTitle:
                                    cutText(_text(title1 + title2 + title3), {
                                        maxPart: 1,
                                        maxLength: 25,
                                    })[0] || '',
                                description:
                                    cutText(_text(description), {
                                        maxPart: 1,
                                        maxLength: 90,
                                    })[0] || '',
                                url,
                            },
                        };

                    default:
                        return self;
                }
            },
            getDefault() {
                return {
                    channel: 'google',
                    creativeType: 'ResponsiveDisplayAd',
                    creative: attachUrls({
                        image: '',
                        squareImage: '',
                        shortTitle: '',
                        description: '',
                        businessName: '',
                    }),
                };
            },
        },
        universalApp: {
            subChannel: ['universal'],
            adType: 'link',
            getDefaultCreative(businessName) {
                var params = this.params;
                var creative = {};
                params.forEach(({ key }) => {
                    if (key === 'title1') {
                        creative[key] = businessName;
                    } else {
                        creative[key] = '';
                    }
                });
                return creative;
            },
            params: [
                {
                    key: 'description1',
                    name: 'PROPOSAL.ad_config_google_description1',
                    type: 'input',
                    maxLength: 25,
                },
                {
                    key: 'description2',
                    name: 'PROPOSAL.ad_config_google_description2',
                    type: 'input',
                    maxLength: 25,
                },
                {
                    key: 'description3',
                    name: 'PROPOSAL.ad_config_google_description3',
                    type: 'input',
                    maxLength: 25,
                },
                {
                    key: 'description4',
                    type: 'textarea',
                    name: 'PROPOSAL.ad_config_google_description4',
                    maxLength: 25,
                },
                {
                    key: 'url',
                    type: 'input',
                    name: 'PROPOSAL.ad_config_all_url',
                },
                ...APP_URL_CONFIG,
            ],
            executable(creative = {}, options) {
                const missings = getMissings(creative, [
                    'description1',
                    'description2',
                    'description3',
                    'description4',
                ]);

                const urls = validateUrls(creative, options);

                return returnReasons([...missings, ...urls]);
            },
            updateFrom(ad = {}, self = {}) {
                if (ad === self) {
                    return self;
                }
                const adCreative = ad.creative;
                const _text = R.compose(R.of, R.defaultTo(''));
                switch (ad.channel) {
                    case 'facebook':
                    case 'instagram':
                        var desc;
                        if (ad.creativeType === 'photo') {
                            desc = adCreative.description;
                        } else {
                            desc = adCreative.mainDescription;
                        }

                        var [
                            description1 = '',
                            description2 = '',
                            description3 = '',
                            description4 = '',
                        ] =
                            cutText(_text(desc), {
                                maxPart: 4,
                                maxLength: 25,
                            }) || [];

                        return {
                            ...self,
                            creative: {
                                ...self.creative,
                                description1,
                                description2,
                                description3,
                                description4,
                            },
                        };
                    case 'google':
                        var desc2;
                        if (ad.creativeType === 'ExpandedTextAd') {
                            desc = adCreative.description;
                            desc2 = adCreative.description2;
                        }

                        [description1 = ''] =
                            cutText(_text(desc), {
                                maxPart: 1,
                                maxLength: 25,
                            }) || [];
                        [description2 = ''] =
                            cutText(_text(desc2), {
                                maxPart: 1,
                                maxLength: 25,
                            }) || [];

                        return {
                            ...self,
                            creative: {
                                ...self.creative,
                                description1,
                                description2,
                                description3,
                                description4,
                            },
                        };
                    default:
                        return self;
                }
            },
        },
        universalAppV2: {
            subChannel: ['universal'],
            adType: 'link',
            params: [
                {
                    key: 'titles',
                },
                {
                    key: 'descriptions',
                },
                {
                    key: 'url',
                    name: 'PROPOSAL.ad_config_all_url',
                },
                ...APP_URL_CONFIG,
            ],
            executable(creative = {}, options) {
                let reasons = [];
                const { titles = [], descriptions = [] } = creative;

                let titlesReason = [];

                titles.forEach((v, index) => {
                    v = v.trim();
                    if (!v) {
                        titlesReason.push({
                            type: 'missing',
                            index,
                        });
                    }
                });

                if (isValueExist(titlesReason)) {
                    reasons = [
                        ...reasons,
                        {
                            type: 'missing',
                            paramName: 'titles',
                            missings: titlesReason,
                        },
                    ];
                }

                let descReasons = [];
                descriptions.forEach((v, index) => {
                    v = v.trim();
                    if (!v) {
                        descReasons.push({
                            type: 'missing',
                            index,
                        });
                    }
                });

                if (isValueExist(descReasons)) {
                    reasons = [
                        ...reasons,
                        {
                            type: 'missing',
                            paramName: 'descriptions',
                            missings: descReasons,
                        },
                    ];
                }

                return returnReasons(reasons);
            },
            updateFrom(ad = {}, self = {}) {
                return self;
            },
            getDefault() {
                return {
                    channel: 'google',
                    creativeType: 'universalAppV2',
                    creative: attachUrls({
                        titles: [],
                        descriptions: [],
                    }),
                };
            },
        },
        CallOnlyAd: {
            subChannel: ['display'],
            adType: 'call',
            getDefaultCreative: defaultFunc,
            params: [
                {
                    key: 'businessName',
                    type: 'input',
                    maxLength: 25,
                    name: 'Business Name',
                },
                {
                    key: 'description',
                    type: 'textarea',
                    maxLength: 160,
                    name: 'Description',
                },
                {
                    key: 'phoneNumber',
                    type: 'phoneNumber',
                    name: 'Phone Number',
                },
                {
                    key: 'url',
                    type: 'input',
                    name: 'Landing URL',
                },
            ],
        },
    },
    facebook: {
        photo: {
            subChannel: [
                'facebook',
                'instagram',
                'audience_network',
                'messenger',
            ],
            // positions: ['feed', 'mobile', 'column'],
            adType: 'link',
            getDefault() {
                return {
                    channel: 'facebook',
                    creativeType: 'photo',
                    creative: attachUrls({
                        media: '',
                        description: '',
                        title: '',
                        linkDescription: '',
                    }),
                };
            },
            params: [
                {
                    ...VIDEO_CONFIG_INFO,
                    maxVideoLength: { value: 60, unit: 'min' },
                    key: 'media',
                    name: 'PROPOSAL.ad_config_all_media',
                    accept: UPLOADABLE_IMAGE_VIDEO_TYPE,
                    recommendedSize: { width: 1080, height: 1080 },
                    format: PHOTO_FORMAT,
                    ratio: '1.91:1',
                    minSize: { width: 500, height: 500 },
                    maxFileSize: 1000,
                    type: 'slides',
                },
                {
                    key: 'description',
                    type: 'textarea',
                    name: 'PROPOSAL.ad_config_facebook_description',
                },
                {
                    key: 'title',
                    type: 'input',
                    optional: true,
                    name: 'PROPOSAL.ad_config_facebook_headline',
                },
                {
                    key: 'linkDescription',
                    type: 'input',
                    maxLength: 90,
                    name: 'PROPOSAL.ad_config_facebook_link_description',
                    optional: true,
                },
                // {
                //     key: 'url',
                //     name: 'PROPOSAL.ad_config_all_url',
                //     type: 'input',
                //     valueFromAll: R.prop('url'),
                // },
                ...APP_URL_CONFIG,
                CTA_PARAM,
            ],
            addGroup: addPhotoGroup,
            executable(creative = {}, options = {}) {
                const reasons = getMissings(creative, ['description', 'media']);
                const urls = validateUrls(creative, options);

                return returnReasons([...reasons, ...urls]);
            },
            updateFrom(ad = {}, self = {}) {
                switch (ad.channel) {
                    case 'instagram':
                    case 'facebook':
                        return {
                            ...self,
                            ...COPY_OMIT(ad),
                            meta: R.clone(ad.meta),
                        };

                    default:
                        return self;
                }
            },
        },
        carousel: {
            subChannel: [
                'facebook',
                'instagram',
                'audience_network',
                'messenger',
            ],
            adType: 'link',
            sharedParams: [{ name: 'mainDescription' }, { name: 'mainUrl' }],
            params: [
                {
                    ...VIDEO_CONFIG_INFO,
                    key: 'media',
                    name: 'PROPOSAL.ad_config_all_media',
                    accept: UPLOADABLE_IMAGE_VIDEO_TYPE,
                    recommendedSize: { width: 1080, height: 1080 },
                    format: PHOTO_FORMAT,
                    ratio: '1.91:1',
                    minSize: { width: 500, height: 500 },
                    maxFileSize: 1000,
                    type: 'slides',
                },
                {
                    type: 'textarea',
                    key: 'mainDescription',
                    name: 'PROPOSAL.ad_config_facebook_carousel_description_common',
                    common: true,
                },
                {
                    key: 'mainUrl',
                    name: 'PROPOSAL.ad_config_carousel_url_common',
                    type: 'input',
                    common: true,
                },
                {
                    key: 'title',
                    type: 'input',
                    maxLength: 40,
                    name: 'PROPOSAL.ad_config_facebook_headline',
                    optional: true,
                },
                {
                    key: 'description',
                    type: 'input',
                    name: 'PROPOSAL.ad_config_facebook_carousel_description',
                },
                // {
                //     key: 'url',
                //     name: 'PROPOSAL.ad_config_all_url',
                //     type: 'input',
                // },
                ...APP_URL_CONFIG,
                CTA_PARAM,
            ],
            addGroup: addCarouselGroup,
            removeGroup: removeCarouselGroup,
            executable(creative = {}, options = []) {
                let reasons = [];

                if (!creative.mainDescription) {
                    reasons.push({
                        type: 'missing',
                        paramName: 'mainDescription',
                        index: 0,
                    });
                }
                const cards = creative.cards;

                if (!cards || cards.length < 2) {
                    reasons.push({ type: 'notEnoughCards' });
                }

                cards.forEach((card, index) => {
                    const { media = '', meta: { media: mediaMeta = {} } = {} } =
                        card;

                    if (!media) {
                        reasons.push({
                            type: 'missing',
                            paramName: 'media',
                            index,
                        });
                    }

                    reasons = reasons.concat(
                        validateUrls(card, options).map(R.assoc('index', index))
                    );

                    if (isVideoType(media)) {
                        if (mediaMeta) {
                            if (mediaMeta.duration > 3600) {
                                reasons.push({
                                    type: 'invalid',
                                    paramName: 'media',
                                    tooLong: 3600,
                                });
                            }
                        }
                    }
                });

                return returnReasons(reasons);
            },
            updateFrom(ad = {}, self = {}) {
                switch (ad.channel) {
                    case 'instagram':
                    case 'facebook':
                        return {
                            ...self,
                            ...COPY_OMIT(ad),
                            meta: R.clone(ad.meta),
                        };

                    default:
                        return self;
                }
            },
        },
    },
    instagram: {
        photo: {
            adType: 'link',
            getDefaultCreative: defaultFunc,
            params: [
                {
                    key: 'media',
                    name: 'PROPOSAL.ad_config_all_media',
                    type: 'slides',
                    ...VIDEO_CONFIG_INFO,
                    maxVideoLength: { value: 1, unit: 'min' },
                    accept: UPLOADABLE_IMAGE_VIDEO_TYPE,
                    recommendedSize: { width: 1080, height: 1080 },
                    format: PHOTO_FORMAT,
                    ratio: '1:1',
                    minSize: { width: 500, height: 500 },
                    maxFileSize: 1000,
                    validate(creative = {}) {
                        const value = creative[this.key];
                        var ret = [];
                        if (!value) {
                            return ret;
                        } else {
                            if (isVideoType(value)) {
                                var meta = R.pathOr(
                                    {},
                                    ['meta', this.key],
                                    creative
                                );
                                if (meta.width < 500 || meta.height < 500) {
                                    ret.push(INSTAGRAM_ERROR_TYPES.SIZE_RATIO);
                                }

                                if (meta.duration > 60) {
                                    ret.push(INSTAGRAM_ERROR_TYPES.DURATION);
                                }
                            }
                        }

                        return ret;
                    },
                },
                {
                    key: 'description',
                    type: 'textarea',
                    name: 'PROPOSAL.ad_config_facebook_description',
                },
                // {
                //     key: 'url',
                //     name: 'PROPOSAL.ad_config_all_url',
                //     type: 'input',
                //     valueFromAll: R.prop('url'),
                // },
                ...APP_URL_CONFIG,
                CTA_PARAM,
            ],
            addGroup: addPhotoGroup,
            executable(creative = {}, options = {}) {
                const reasons = getMissings(creative, ['media']);
                const urls = validateUrls(creative, options);

                return returnReasons([...reasons, ...urls]);
            },
            updateFrom(ad = {}, self = {}) {
                switch (ad.channel) {
                    case 'facebook':
                    case 'instagram':
                        return {
                            ...self,
                            ...COPY_OMIT(ad),
                            meta: R.clone(ad.meta),
                        };

                    default:
                        return self;
                }
            },
            getDefault() {
                return {
                    channel: 'instagram',
                    creativeType: 'photo',
                    creative: attachUrls({
                        media: '',
                        description: '',
                        title: '',
                        linkDescription: '',
                    }),
                };
            },
        },
        carousel: {
            adType: 'link',
            sharedParams: [{ name: 'mainDescription' }, { name: 'mainUrl' }],
            params: [
                {
                    ...VIDEO_CONFIG_INFO,
                    maxVideoLength: { value: 1, unit: 'min' },
                    key: 'media',
                    name: 'PROPOSAL.ad_config_all_media',
                    type: 'slides',
                    accept: UPLOADABLE_IMAGE_VIDEO_TYPE,
                    recommendedSize: { width: 1080, height: 1080 },
                    format: PHOTO_FORMAT,
                    ratio: '1.91:1',
                    minSize: { width: 500, height: 500 },
                    maxFileSize: 1000,
                    recommendedVideoRatio: { h: 1, v: 1 },
                    validate(creative = {}) {
                        const value = creative[this.key];
                        var ret = [];
                        if (!value) {
                            return ret;
                        } else {
                            if (isVideoType(value)) {
                                var meta = R.pathOr(
                                    {},
                                    ['meta', this.key],
                                    creative
                                );
                                if (meta.width < 500 || meta.height < 500) {
                                    ret.push(INSTAGRAM_ERROR_TYPES.SIZE_RATIO);
                                }

                                if (meta.duration > 120) {
                                    ret.push(
                                        INSTAGRAM_ERROR_TYPES.DURATION_CAROUSEL_VIDEO
                                    );
                                }
                            }
                        }

                        return ret;
                    },
                },
                {
                    type: 'textarea',
                    key: 'mainDescription',
                    name: 'PROPOSAL.ad_config_facebook_carousel_description_common',
                    common: true,
                },
                {
                    key: 'mainUrl',
                    name: 'PROPOSAL.ad_config_carousel_url_common',
                    type: 'input',
                    common: true,
                },
                {
                    key: 'title',
                    type: 'input',
                    maxLength: 40,
                    name: 'PROPOSAL.ad_config_facebook_headline',
                    optional: true,
                },
                {
                    key: 'description',
                    type: 'input',
                    name: 'PROPOSAL.ad_config_facebook_description',
                    optional: true,
                },
                ...APP_URL_CONFIG,
                CTA_PARAM,
            ],
            addGroup: addCarouselGroup,
            removeGroup: removeCarouselGroup,
            executable(creative = {}, options = {}) {
                let reasons = [];
                if (!creative.mainDescription) {
                    reasons.push({
                        type: 'missing',
                        paramName: 'mainDescription',
                        index: 0,
                    });
                }
                const cards = creative.cards;
                if (!cards || cards.length < 2) {
                    reasons.push({
                        type: 'notEnoughCards',
                    });
                }

                cards.forEach((card, index) => {
                    const { media = '', meta: { media: mediaMeta = {} } = {} } =
                        card;

                    if (!media) {
                        reasons.push({
                            type: 'missing',
                            paramName: 'media',
                            index,
                        });
                    }

                    reasons = reasons.concat(
                        validateUrls(card, options).map(R.assoc('index', index))
                    );

                    if (isVideoType(media)) {
                        if (mediaMeta) {
                            if (mediaMeta.ratio != 1) {
                                reasons.push({
                                    type: 'invalid',
                                    paramName: 'media',
                                    ratio: 1,
                                    index,
                                });
                            }
                            if (
                                mediaMeta.width < 500 ||
                                mediaMeta.height < 500
                            ) {
                                reasons.push({
                                    type: 'invalid',
                                    paramName: 'media',
                                    minWidth: 500,
                                    index,
                                });
                            }

                            if (mediaMeta.duration > 120) {
                                reasons.push({
                                    type: 'invalid',
                                    paramName: 'media',
                                    tooLong: 120,
                                    index,
                                });
                            }
                        }
                    }
                });

                return returnReasons(reasons);
            },
            updateFrom(ad = {}, self = {}) {
                switch (ad.channel) {
                    case 'facebook':
                    case 'instagram':
                        return {
                            ...self,
                            ...COPY_OMIT(ad),
                            meta: R.clone(ad.meta),
                        };

                    default:
                        return self;
                }
            },
        },
    },
    apple: {
        search: {
            adType: 'link',
            params: [
                {
                    key: 'mobileAppInfo',
                },
            ],
            getDefault() {
                return {
                    channel: 'apple',
                    creativeType: 'search',
                    creative: attachUrls({
                        mobileAppInfo: {
                            title: '',
                            screenshotUrls: '',
                            logo: '',
                            rating: '',
                            ratingCount: '',
                        },
                    }),
                };
            },
        },
    },
    kakao: {
        image: {
            adType: 'link',
            params: [
                {
                    key: 'image',
                    type: 'image',
                    format: PHOTO_FORMAT,
                    maxFileSize: 1000,
                },
                {
                    key: 'logo',
                    type: 'image',
                    format: PHOTO_FORMAT,
                    maxFileSize: 1000,
                },
                {
                    key: 'profileName',
                    type: 'input',
                    maxLength: 20,
                },
                {
                    key: 'description',
                    type: 'input',
                    maxLength: 45,
                },
                {
                    key: 'title',
                    type: 'input',
                    maxLength: 25,
                },
                {
                    key: 'url',
                    type: 'input',
                },
                CTA_PARAM,
            ],
            executable(creative = {}, options) {
                const missings = getMissings(creative, [
                    'image',
                    'logo',
                    'profileName',
                    'title',
                    'description',
                ]);

                const urls = validateUrls(creative, options);

                return returnReasons([...missings, ...urls]);
            },
            getDefault() {
                return {
                    channel: 'kakao',
                    creativeType: 'image',
                    creative: attachUrls({
                        image: '',
                        logo: '',
                        profileName: '',
                        description: '',
                        title: '',
                    }),
                };
            },
            counter: R.always(1),
        },
    },
    [CHANNELS.MICROSOFT]: {
        ExpandedTextAd: {
            adType: 'link',
            subChannel: ['bing'],
            params: [
                {
                    key: 'title1',
                    type: 'input',
                    maxLength: 30,
                },
                {
                    key: 'title2',
                    type: 'input',
                    maxLength: 30,
                },
                {
                    key: 'title3',
                    type: 'input',
                    maxLength: 30,
                },
                {
                    key: 'text1',
                    type: 'input',
                    maxLength: 90,
                },
                {
                    key: 'text2',
                    type: 'input',
                    maxLength: 90,
                },
                {
                    key: 'url',
                    type: 'input',
                },
            ],
            executable(creative = {}, options) {
                const missings = getMissings(creative, [
                    'title1',
                    'title2',
                    'text1',
                ]);

                const urls = validateUrls(creative, options);

                return returnReasons([...missings, ...urls]);
            },
            getDefault() {
                return {
                    channel: CHANNELS.MICROSOFT,
                    creativeType: 'ExpandedTextAd',
                    creative: {
                        url: '',
                        title1: '',
                        title2: '',
                        title3: '',
                        text1: '',
                        text2: '',
                    },
                };
            },
        },
    },
    [CHANNELS.YOUTUBE]: {
        video: {
            adType: 'video',
            subChannel: [],
            params: [
                {
                    key: 'media',
                    type: 'slides',
                },
                {
                    key: 'url',
                    type: 'input',
                    maxLength: 255,
                },
                {
                    key: 'displayUrl',
                    type: 'input',
                    maxLength: 255,
                },
                {
                    key: 'callToAction',
                    type: 'input',
                    maxLength: 10,
                },
                {
                    key: 'headline',
                    type: 'input',
                    maxLength: 15,
                },
            ],
            executable(creative = {}, options) {
                const requiredParams = ['displayUrl'];

                const { hasCta } = creative;
                if (hasCta) requiredParams.push('callToAction');

                const missings = getMissings(creative, requiredParams);
                const urls = validateUrls(creative, options);

                return returnReasons([...missings, ...urls]);
            },
            getDefault() {
                return {
                    channel: CHANNELS.YOUTUBE,
                    creativeType: 'video',
                    creative: attachUrls({
                        media: '',
                        displayUrl: '',
                        callToAction: '',
                        headline: '',
                        thumbnail: '',
                        hasCta: false,
                    }),
                };
            },
        },
    },
};

export default CONFIG;
